import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

/** Query key prefix shared by project detail, member search and page lists. */
export const adminProjectKeys = {
  all: ["admin", "project"] as const,
  detail: (id: string) => ["admin", "project", id] as const,
  availableUsers: (id: string, search: string) =>
    ["admin", "project", id, "available-users", search] as const,
  // The resource lists are infinite queries ("Afficher plus"): one key per
  // search, so the loader prefetch and the tab read the same cache entry.
  qrCodes: (projectId: string, search: string) =>
    ["admin", "project", projectId, "qr-codes", { search: search.trim() }] as const,
  posters: (projectId: string, search: string, type?: "wifi" | "printer") =>
    ["admin", "project", projectId, "posters", { search: search.trim(), type }] as const,
};

/** The canonical project list key, shared by the page and its intent prefetch. */
export const adminProjectsKey = ["admin", "projects"] as const;

/** Project row as the admin projects list shows it (GET /api/admin/projects). */
export interface AdminProject {
  readonly id: string;
  readonly projectNumber: string;
  readonly name: string;
  readonly description: string | null;
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly location: string | null;
  readonly googleMapsUrl: string | null;
  readonly isActive: boolean;
  readonly createdBy: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly memberCount?: number;
  readonly calendarReadOnly?: boolean;
  readonly weatherCollectionPaused?: boolean;
  readonly calendarSetupCompleted?: boolean;
  readonly moduleCount?: number;
  readonly managerCount?: number;
  readonly weatherNeedsLocation?: boolean;
  readonly placeName?: string | null;
  /** Set when the PSU sync closed the project because its folder was archived. */
  readonly psuClosedAt?: string | null;
}

export interface ProjectMember {
  projectId: string;
  userId: string;
  role: "MEMBER" | "SUBMANAGER" | "MANAGER";
  createdAt: string;
  user?: {
    id: string;
    email: string;
    fullName?: string;
    status: string;
    avatarUrl?: string;
    roleId?: number | null;
  };
}

/** Full project detail (GET /api/admin/projects/:id): row + members. */
export interface ProjectDetail extends AdminProject {
  readonly members: ProjectMember[];
  readonly memberCount: number;
}

export interface UserOption {
  id: string;
  email: string;
  fullName: string | null;
}

export interface ProjectQrCodeRow {
  readonly id: string;
  readonly name: string;
  readonly createdBy: string;
  readonly projectId: string;
  readonly pdfFileKey: string | null;
  readonly qrCodeValue: string;
  readonly totalViews: number;
  readonly totalDownloads: number;
  readonly createdAt: string;
  readonly lastModified: string;
  readonly lastAccessed: string | null;
  readonly isDeleted: boolean;
  readonly fileMetadata: { size: number; contentType: string; originalName: string } | null;
}

export interface ProjectQrCodeList {
  items: ProjectQrCodeRow[];
  pagination: { page: number; limit: number; totalItems: number; totalPages: number };
}

export interface ProjectPosterRow {
  readonly id: string;
  readonly type: "wifi" | "printer";
  readonly name: string;
  readonly description: string | null;
  readonly ssid: string | null;
  readonly password: string | null;
  readonly encryption: string | null;
  readonly manufacturer: string | null;
  readonly model: string | null;
  readonly ipAddress: string | null;
  readonly driverUrl: string | null;
  readonly photoFileKey: string | null;
  readonly pdfFileKey: string | null;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly isDeleted: boolean;
  readonly pdfDownloadUrl: string | null;
}

export interface ProjectPosterList {
  data: ProjectPosterRow[];
  pagination: { total: number; page: number; pageSize: number; pageCount: number };
}

/** The full project list. Shared by the list page and its intent prefetch. */
export const adminProjectsQueryOptions = queryOptions({
  queryKey: adminProjectsKey,
  queryFn: async (): Promise<AdminProject[]> => {
    const res = await fetch("/api/admin/projects", { cache: "no-store" });

    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new Error(String(data?.message ?? `HTTP ${res.status}`));
    }

    // SAFETY: response from our own /api/admin/projects endpoint is the
    // AdminProject[] contract.
    return res.json() as Promise<AdminProject[]>;
  },
});

/** One project's detail. Shared by the detail page and its intent prefetch. */
export const adminProjectQueryOptions = (id: string) =>
  queryOptions({
    queryKey: adminProjectKeys.detail(id),
    queryFn: async (): Promise<ProjectDetail> => {
      const res = await fetch(`/api/admin/projects/${id}`, { cache: "no-store" });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(String(data?.message ?? `HTTP ${res.status}`));
      }

      // SAFETY: success body of our own /api/admin/projects/{id} endpoint is
      // the documented ProjectDetail shape (res.ok checked above).
      return res.json() as Promise<ProjectDetail>;
    },
  });

/**
 * Available users for the add-member picker, walked page by page up to a hard
 * cap so members past the first page remain selectable.
 */
export const adminProjectAvailableUsersOptions = (projectId: string, search: string) =>
  queryOptions({
    queryKey: adminProjectKeys.availableUsers(projectId, search),
    queryFn: async (): Promise<UserOption[]> => {
      const term = search.trim();
      const baseParams = new URLSearchParams({ projectId, limit: "500" });

      if (term) baseParams.set("search", term);

      const fetchPage = async (page: number) => {
        const params = new URLSearchParams(baseParams);

        if (page > 1) params.set("page", String(page));

        const res = await fetch(`/api/admin/users/available?${params.toString()}`, {
          cache: "no-store",
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        // SAFETY: success body of our own /api/admin/users/available endpoint
        // is the documented paginated { data, pagination } shape.
        return res.json() as Promise<{
          data?: UserOption[];
          pagination?: { pageCount?: number };
        }>;
      };

      const firstPage = await fetchPage(1);
      let users = firstPage.data ?? [];
      const pageCount = Math.min(firstPage.pagination?.pageCount ?? 1, 10);

      for (let page = 2; page <= pageCount; page++) {
        const nextPage = await fetchPage(page);
        users = users.concat(nextPage.data ?? []);
      }

      return users;
    },
    enabled: search.trim().length > 0,
  });

const RESOURCE_PAGE_SIZE = 10;

/** A project's QR codes, a page at a time; `total` comes with every page. */
export const projectQrCodesQueryOptions = (projectId: string, search: string) =>
  infiniteQueryOptions({
    queryKey: adminProjectKeys.qrCodes(projectId, search),
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<ProjectQrCodeList> => {
      const qs = new URLSearchParams({
        page: String(pageParam),
        limit: String(RESOURCE_PAGE_SIZE),
      });

      if (search.trim()) qs.set("search", search.trim());

      const res = await fetch(`/api/admin/projects/${projectId}/qr-codes?${qs.toString()}`, {
        cache: "no-store",
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // SAFETY: success body of our own QR-codes endpoint is the documented
      // ProjectQrCodeList shape (res.ok checked above).
      return res.json() as Promise<ProjectQrCodeList>;
    },
    getNextPageParam: (last) =>
      last.pagination.page < last.pagination.totalPages ? last.pagination.page + 1 : undefined,
  });

/** A project's posters of one type, a page at a time. */
export const projectPostersQueryOptions = (
  projectId: string,
  search: string,
  type?: "wifi" | "printer",
) =>
  infiniteQueryOptions({
    queryKey: adminProjectKeys.posters(projectId, search, type),
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<ProjectPosterList> => {
      const qs = new URLSearchParams({
        page: String(pageParam),
        limit: String(RESOURCE_PAGE_SIZE),
        sort: "createdAt",
        order: "desc",
        projectId,
      });

      if (search.trim()) qs.set("search", search.trim());

      if (type) qs.set("type", type);
      const res = await fetch(`/api/admin/posters?${qs.toString()}`, { cache: "no-store" });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // SAFETY: success body of our own /api/admin/posters endpoint is the
      // ProjectPosterList shape (res.ok checked above).
      return res.json() as Promise<ProjectPosterList>;
    },
    getNextPageParam: (last) =>
      last.pagination.page < last.pagination.pageCount ? last.pagination.page + 1 : undefined,
  });
